/**
PW Custom Scroll bars
**/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import DimensionAware from '../../hoc/DimensionAware'
import Timing from '../../hoc/Timing'
import cx from 'classnames'
import { getEventCoordinate, getBoundsForNode } from '../../utils'
import easing from 'easing-js'
import ScrollBarsImpl from './ScrollBarsImpl'

const { assign, pick } = _

const ANIM_END_EVENTS = [
  'animationend',
  'transitionend',
  'webkitAnimationEnd',
  'webkitTransitionEnd'
]

// One-time measurement of the native scrollbar's width - delayed until first access
// to give CSS that might affect the result as long as possible to be fully applied
const getScrollerStyles = (function() {
  let cache = null
  return function() {
    if (!cache) {
      const div = document.createElement('div')
      div.className = 'pw_scroll_scroller'
      assign(div.style, {
        position: 'absolute',
        top: '-1000px',
        overflow: 'scroll',
        padding: 0,
        border: 0
      })
      document.body.appendChild(div)
      const barSize = div.offsetWidth - div.clientWidth || 0
      document.body.removeChild(div)
      cache = {
        marginRight: -barSize,
        marginBottom: -barSize
      }
    }
    return cache
  }
})()

class ScrollBars extends React.Component {
  static displayName = 'ScrollBars'

  static propTypes = {
    autoscrollOnMouseDrag: T.bool, // If true will autoscroll to dragged mouse position
    autoscrollRate: T.number, // px to scroll on each "tick" when autoscrolling is required
    autoscrollTolerance: T.number, // px
    children: T.any,
    className: T.string,
    classes: T.string, //deprecated
    horizontal: T.bool,
    onScroll: T.func,
    outside: T.bool,
    slimShady: T.bool, // If true, will show scroll indicators when content is hidden out of view
    slimShadyDarker: T.bool,
    thumbMinLength: T.number,
    vertical: T.bool,

    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired,

    height: T.number,
    width: T.number
  }

  static contextTypes = {
    dragSelection: T.object
  }

  static defaultProps = {
    autoscrollOnMouseDrag: false,
    autoscrollRate: 20,
    autoscrollTolerance: 20,
    horizontal: true,
    outside: false,
    thumbMinLength: 30,
    slimShady: false,
    slimShadyDarker: false,
    vertical: true
  }

  UNSAFE_componentWillMount() {
    this._throttledScrollCheck = _.debounce(this._checkIfAutoscrollNeeded, 50)
  }

  componentDidMount() {
    ANIM_END_EVENTS.forEach(name => {
      ReactDOM.findDOMNode(this._contentRef).addEventListener(
        name,
        this._queueDimensionsUpdate,
        false
      )
    })
    this._queueDimensionsUpdate()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.height !== this.props.height ||
      nextProps.width !== this.props.width
    ) {
      this._queueDimensionsUpdate()
    }
  }

  componentDidUpdate() {
    this._queueDimensionsUpdate()
  }

  componentWillUnmount() {
    this._isUnmounting = true
    ANIM_END_EVENTS.forEach(name => {
      ReactDOM.findDOMNode(this._contentRef).removeEventListener(
        name,
        this._queueDimensionsUpdate,
        false
      )
    })
  }

  scrollToDelta = (deltaX, deltaY) => {
    const el = this.getScrolledElement()
    this.scrollTo(el.scrollLeft + deltaX, el.scrollTop + deltaY)
  }

  scrollTo = (x, y) => {
    if (this._isUnmounting) {
      return
    }
    const el = this.getScrolledElement()
    if (el) {
      el.scrollLeft = x
      el.scrollTop = y
    }
  }

  scrollToEnd = () => {
    const node = this.getScrolledElement()
    this.scrollTo(0, node.scrollHeight)
  }

  scrollToAnimate = (
    duration = 5000,
    newScrollTop = null,
    newScrollLeft = null,
    easingFunction = 'easeInOutCubic'
  ) => {
    if (!easingFunction || !easing[easingFunction]) {
      console.error(
        `ScrollBars@scrollToAnimate: Invalid easing function: "${easingFunction}"`
      )
      return
    }
    const easingFn = easing[easingFunction]
    const el = this.getScrolledElement()
    if (!el || this._isUnmounting) {
      return
    }
    const currentScrollTop = el.scrollTop
    const currentScrollLeft = el.scrollLeft
    const scrollTopDelta = isNaN(newScrollTop)
      ? 0
      : newScrollTop - currentScrollTop
    const scrollLeftDelta = isNaN(newScrollLeft)
      ? 0
      : newScrollLeft - currentScrollLeft
    const _startTime = Date.now()

    const _animatedScrollTick = timeElapsed => {
      timeElapsed = Date.now() - _startTime
      if (scrollTopDelta !== 0) {
        el.scrollTop = easingFn(
          timeElapsed,
          currentScrollTop,
          scrollTopDelta,
          duration
        )
      }
      if (scrollLeftDelta !== 0) {
        el.scrollLeft = easingFn(
          timeElapsed,
          currentScrollLeft,
          scrollLeftDelta,
          duration
        )
      }
      if (timeElapsed < duration && !this._isUnmounting) {
        requestAnimationFrame(() => {
          if (!this._isUnmounting) {
            _animatedScrollTick(timeElapsed)
          }
        })
      }
    }
    _animatedScrollTick(0)
  }

  resetScrollPosition = () => {
    this.scrollTo(0, 0)
  }

  resetScrollPositionAnimate = () => {
    this.scrollToAnimate(null, 0, 0)
  }

  getScrolledElement = () => {
    if (!this.isUnmounting) {
      return ReactDOM.findDOMNode(this._scrollerRef)
    }
  }

  getScrollPosition = () => {
    const node = this.getScrolledElement()
    if (node) {
      const scrollWindowBottom = node.scrollTop + node.offsetHeight
      return {
        scrollTop: node.scrollTop,
        scrollBottom: scrollWindowBottom,
        distanceFromEnd: node.scrollHeight - scrollWindowBottom // Bottom of scrolled viewport
      }
    }
  }

  getContentRef = () => {
    return this._contentRef
  }

  getScrollerRef = () => {
    return this._scrollerRef
  }

  getScrollerImplRef = () => {
    return this._scrollerImplRef
  }

  _queueDimensionsUpdate = () => {
    this.props.setTimer(this._updateDimensions, 'frame', 'dimensions')
  }

  _updateDimensions = () => {
    if (!this.isUnmounting) {
      const el = ReactDOM.findDOMNode(this._scrollerRef)
      const state = pick(
        el,
        'clientWidth',
        'clientHeight',
        'scrollWidth',
        'scrollHeight',
        'scrollLeft',
        'scrollTop'
      )
      this._scrollerImplRef.setStatePlzThx(state)
    }
  }

  _onScroll = e => {
    const node = e.currentTarget
    const state = pick(node, 'scrollLeft', 'scrollTop')
    this._scrollerImplRef.setStatePlzThx(state)

    // Broadcast scroll dimensions to any parent dragSelection instance
    if (this.context.dragSelection) {
      this.context.dragSelection.onScrolled(state)
    }
    if (typeof this.props.onScroll === 'function') {
      this.props.onScroll(node)
    }
  }

  _onWheel = e => {
    // Hack to prevent accidental back/forward navigation on Mac browsers when using touchpad
    // to scroll horizontally. If the content is scrollable, and is already scrolled to the far
    // left or right, call preventDefault on the wheel event so it's ignored as a swipe gesture.
    const { deltaX, deltaY } = e
    if (Math.abs(deltaY) < Math.abs(deltaX)) {
      const el = ReactDOM.findDOMNode(this._scrollerRef)
      if (
        ((deltaX < 0 && el.scrollLeft === 0) ||
          (deltaX > 0 && el.scrollLeft + el.clientWidth === el.scrollWidth)) &&
        (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
      ) {
        e.preventDefault()
      }
    }
  }

  _onThumbWheel = e => {
    // Mouse wheel events directly on the scrollbar thumb will not cause a corresponding scroll
    // in the main element, so we need to perform the scroll manually.
    const node = this.getScrolledElement()
    if (node) {
      this.scrollTo(node.scrollLeft + e.deltaX, node.scrollTop + e.deltaY)
    }
    e.preventDefault()
  }

  /**
   * Autoscroll related methods
   */
  _checkIfAutoscrollNeeded = () => {
    if (this.props.autoscrollOnMouseDrag && this._mouseDownData) {
      const { pageX, pageY } = this._mouseDownData
      // Check to see if a child scrollBars instance needs to be autoscrolled
      if (
        pageY >
        this._boundingBox.top +
          this._boundingBox.offsetHeight -
          this.props.autoscrollTolerance
      ) {
        this.scrollToDelta(0, this.props.autoscrollRate) // Scroll Down
      } else if (
        pageY <
        this._boundingBox.top + this.props.autoscrollTolerance
      ) {
        this.scrollToDelta(0, -this.props.autoscrollRate) // Scroll Up
      } else if (
        pageX <
        this._boundingBox.left + this.props.autoscrollTolerance
      ) {
        this.scrollToDelta(-this.props.autoscrollRate, 0) // Scroll Left
      } else if (
        pageX >
        this._boundingBox.left +
          this._boundingBox.offsetWidth -
          this.props.autoscrollTolerance
      ) {
        this.scrollToDelta(this.props.autoscrollRate, 0) // Scroll Right
      }
    }

    // Keep checking as long as mouse key is down
    if (this._mouseIsDown) {
      this._throttledScrollCheck()
    }
  }

  _onMouseUp = () => {
    this._mouseIsDown = false
    window.document.removeEventListener('mousemove', this._getMouseDownData)
    window.document.removeEventListener('touchmove', this._getMouseDownData)
    window.document.removeEventListener('mouseup', this._onMouseUp)
    window.document.removeEventListener('touchend', this._onMouseUp)

    if (!this._mouseDownData) {
      return
    }

    this._mouseDownData = null
  }

  _onMouseDown = e => {
    // Disable if target is control by react-dnd
    if (e.target.draggable) {
      return
    }

    this._mouseIsDown = true

    window.document.addEventListener('mouseup', this._onMouseUp)
    window.document.addEventListener('touchend', this._onMouseUp)

    // Right clicks
    if (e.which === 3 || e.button === 2) {
      return
    }

    this._boundingBox = getBoundsForNode(ReactDOM.findDOMNode(this))

    this._getMouseDownData(e)
    window.document.addEventListener('mousemove', this._getMouseDownData)
    this._checkIfAutoscrollNeeded()

    // NOTE: we intentionally don't e.preventDefault() here because some usages may want native
    // text selection to continue to occur. If you don't, then you'll want to either call that
    // yourself or set user-select:none in CSS.
  }

  _getMouseDownData = e => {
    this._mouseDownData = {
      pageX: getEventCoordinate(e, 'pageX'),
      pageY: getEventCoordinate(e, 'pageY')
    }
  }

  _bindScrollerRef = ref => {
    this._scrollerRef = ref
  }

  _bindContentRef = ref => {
    this._contentRef = ref
  }

  _bindScrollerImplRef = ref => {
    this._scrollerImplRef = ref
  }

  render() {
    const {
      autoscrollOnMouseDrag,
      children,
      classes,
      className,
      horizontal,
      outside,
      slimShady,
      slimShadyDarker,
      thumbMinLength,
      vertical
    } = this.props

    return (
      <div
        className={cx('pw_scrollbars', classes, className, {
          outside: outside
        })}
        onMouseDown={autoscrollOnMouseDrag ? this._onMouseDown : _.noop}
        onTouchStart={autoscrollOnMouseDrag ? this._onMouseDown : _.noop}
      >
        <div className="pw_scroll_clip">
          <div
            className="pw_scroll_scroller"
            onScroll={this._onScroll}
            onWheel={this._onWheel}
            ref={this._bindScrollerRef}
            style={getScrollerStyles()}
          >
            <div className="pw_scroll_content" ref={this._bindContentRef}>
              {children}
            </div>
          </div>
        </div>
        <ScrollBarsImpl
          horizontal={horizontal}
          onThumbScroll={this.scrollTo}
          onWheel={this._onThumbWheel}
          getRef={this._bindScrollerImplRef}
          slimShady={slimShady}
          slimShadyDarker={slimShadyDarker}
          thumbMinLength={thumbMinLength}
          vertical={vertical}
        />
      </div>
    )
  }
}

export default DimensionAware(Timing(ScrollBars))
